import { useEffect, useState } from 'react'
import { EmptyRow, ErpTable, Td, Th } from '@/components/erp/primitives'
import { ErrorStrip, LoadingRows } from '@/shared/ui/states'
import { useCan } from '@/shared/auth/session'
import { fmtDateTime, humanise } from '@/shared/lib/format'
import { explain } from '@/shared/api/problem'
import { listAudit } from '@/features/audit/api'
import type { AuditEntryResponse, ProgramResponse } from '@/shared/api/types'

/**
 * The programme's audit trail, filtered to this entity.
 *
 * ## Field NAMES, never values
 *
 * A PII reveal on this feature records that a phone was shown and to whom — not
 * what the phone number is. `DiffBuilder` renders values for everything that is
 * not a coded enum, so pointing it at an encrypted column would write the
 * plaintext into the audit log permanently. The backend forbids it; this screen
 * shows what survives.
 */
export function ProgramAuditPanel({ program }: { program: ProgramResponse }) {
  const canRead = useCan('audit:read')
  const [rows, setRows] = useState<AuditEntryResponse[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!canRead) return
    let alive = true
    listAudit({ entityType: 'program', entityId: program.id } as never, undefined, 50)
      .then((page) => { if (alive) setRows(page.content) })
      .catch((e) => { if (alive) setError(explain(e)) })
    return () => { alive = false }
  }, [program.id, canRead])

  if (!canRead) {
    return (
      <p className="px-1 text-erp-xs text-muted-foreground">
        Reading the audit trail needs the <code>audit:read</code> authority.
      </p>
    )
  }

  return (
    <div className="space-y-2 px-1">
      <ErrorStrip message={error} />
      <ErpTable head={(
        <>
          <Th>When</Th>
          <Th>Action</Th>
          <Th>Who</Th>
          <Th>Changed</Th>
        </>
      )}>
        {!rows ? <LoadingRows colSpan={4} />
          : rows.length === 0 ? <EmptyRow colSpan={4} message="Nothing recorded for this programme yet." />
          : rows.map((r) => (
            <tr key={r.id} className="erp-row">
              <Td className="text-erp-xs whitespace-nowrap">{fmtDateTime(r.occurredAt)}</Td>
              <Td className="text-erp-xs">{humanise(r.action)}</Td>
              <Td className="text-erp-xs">{r.actorLabel ?? '—'}</Td>
              <Td className="text-erp-xs text-muted-foreground">
                {r.changes && r.changes.length
                  ? r.changes.map((c) => c.field).join(', ')
                  : '—'}
              </Td>
            </tr>
          ))}
      </ErpTable>
      <p className="text-erp-xs text-muted-foreground">
        A reveal of somebody's phone or national ID records the field name and the reader —
        never the value.
      </p>
    </div>
  )
}
